import { faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { AxiosError } from 'axios';
import { useContext, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import PageHeader from '@/components/PageHeader';
import emptySelectOption from '@/constants/emptySelectOption';
import AppContext from '@/contexts/appContext';
import { useCreatePersonAchievement } from '@/hooks/useAchievement';
import PersonContext from '@/pages/Person/PersonContext';
import { AchievementOverview, Achievement } from '@/types/achievement';
import { Alert } from '@/ui/Alert';
import Button from '@/ui/components/Button';
import Container from '@/ui/components/Container';
import Select from '@/ui/components/Form/Select';
import { defaultOptionTypes } from '@/ui/components/Form/Select/types';
import Tooltip from '@/ui/components/Tooltip';
import mapAxiosErrorMessages from '@/utils/mapAxiosErrorMessages';
import queryClient from '@/utils/testing/queryClient';

const PersonAchievement = () => {
  const navigate = useNavigate();
  const { addMessage } = useContext(AppContext);
  const { person } = useContext(PersonContext);
  const { state } = useLocation();
  const achievementsOverview: AchievementOverview[] = state?.achievements;
  const [selectedAchievementOption, setSelectedAchievementOption] =
    useState<defaultOptionTypes | null>(null);
  const [celebrate, setCelebrate] = useState(true);
  const [selectedAchievement, setSelectedAchievement] = useState<Achievement | null>(null);
  const { mutate: awardAchievement } = useCreatePersonAchievement({
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['achievements'] });
      addMessage({ level: 'success', text: 'Achievement awarded successfully' });
      navigate(-1);
    },
    onError: (error: AxiosError) => {
      addMessage(mapAxiosErrorMessages(error));
    }
  });

  const handleAwardAchievement = () => {
    if (!selectedAchievement) {
      return;
    }
    awardAchievement({
      person: person.id,
      achievement: selectedAchievement.id,
      celebrate
    });
  };

  const notEarnedAchievementsOverviewFlat = useMemo(
    () =>
      achievementsOverview
        .flatMap((category) =>
          category.achievements.map((achievement) => ({
            ...achievement,
            categoryName: category.name
          }))
        )
        .filter((achievement) => !achievement.earned_at),
    [achievementsOverview]
  );

  const achievementsOptions = useMemo(
    () =>
      notEarnedAchievementsOverviewFlat.map((achievement) => ({
        value: achievement.title,
        label: `${achievement.categoryName} - ${achievement.title}`
      })),
    [notEarnedAchievementsOverviewFlat]
  );

  useEffect(() => {
    if (selectedAchievementOption) {
      const selected = notEarnedAchievementsOverviewFlat.find(
        (achievement) => achievement.title === selectedAchievementOption
      );
      const selectedWithoutCategory = selected ? { ...selected, categoryName: undefined } : null;
      setSelectedAchievement(selectedWithoutCategory);
    }
  }, [selectedAchievementOption, notEarnedAchievementsOverviewFlat]);

  return (
    <Container type="page">
      <PageHeader headingText="Award Achievement" />
      <Alert
        message="This action awards only one achievement. If it’s in a series, please be sure to award the next unawarded item. Any skipped achievements will not be retroactively awarded."
        variant="warning"
      />
      <div className="mb-4">
        <Select
          label="New Achievement"
          onChange={(achievementOption) => setSelectedAchievementOption(achievementOption.value)}
          className="w-full border rounded-sm px-3 py-2"
          options={achievementsOptions}
          placeholder="Select the Achievement"
          selectedOption={
            achievementsOptions.find((option) => option.value === selectedAchievementOption) ??
            emptySelectOption
          }
        />
      </div>

      {selectedAchievement && (
        <div className="mb-4 flex flex-col items-center">
          {selectedAchievement.asset_url && (
            <img
              src={selectedAchievement.asset_url}
              alt={selectedAchievement.title}
              className="w-60 h-60"
            />
          )}
          <label className="flex items-center mt-4">
            <input
              type="checkbox"
              checked={celebrate}
              onChange={(e) => setCelebrate(e.target.checked)}
              className="mr-2"
            />
            <Tooltip
              tooltipContent={
                'Check this box if you would like the member to receive a notification of their achievement.'
              }>
              Celebrate &nbsp;
              <FontAwesomeIcon icon={faInfoCircle} />
            </Tooltip>
          </label>

          <Button
            ariaLabel="Award Achievement"
            className="w-full border rounded-sm px-3 py-2 mt-4"
            onClick={handleAwardAchievement}>
            Award &quot;{selectedAchievement.title}&ldquo; Achievement
          </Button>
        </div>
      )}
    </Container>
  );
};

export default PersonAchievement;
